import { addDocument, setDocument, getDocumentById, queryDocuments, getCollectionDocuments } from "../firebase/firestore";

const TEAMS_COLLECTION = "teams";
const USERS_COLLECTION = "users";

function generateTeamCode() {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 8; i += 1) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
}

async function generateUniqueTeamCode() {
    let attempts = 0;
    while (attempts < 10) {
        const code = generateTeamCode();
        const existing = await queryDocuments(TEAMS_COLLECTION, [["teamCodeLower", "==", code.toLowerCase()]]);
        if (!existing || existing.length === 0) {
            return code;
        }
        attempts += 1;
    }
    throw new Error("Failed to generate unique team code");
}

export async function createStudentTeam(leaderId, teamName) {
    if (!leaderId) {
        throw new Error("Invalid leader");
    }
    if (!teamName || !teamName.trim()) {
        throw new Error("Team name is required");
    }
    const leaderProfile = await getDocumentById(USERS_COLLECTION, leaderId);
    if (!leaderProfile || leaderProfile.role !== "student") {
        throw new Error("Only students can create teams");
    }
    const existingTeams = leaderProfile.teams || [];
    const teamCode = await generateUniqueTeamCode();
    const teamData = {
        teamName: teamName.trim(),
        teamCode,
        teamCodeLower: teamCode.toLowerCase(),
        leaderId,
        leaderName: leaderProfile.displayName || leaderProfile.firstName || "",
        leaderEmail: leaderProfile.email || "",
        leaderCollege: leaderProfile.collegeName || leaderProfile.college || "",
        members: [leaderId],
        memberIds: [leaderId],
        teamType: "student",
        createdBy: leaderId,
        createdAt: new Date(),
        updatedAt: new Date()
    };
    const teamId = await addDocument(TEAMS_COLLECTION, teamData);
    const updatedTeams = Array.isArray(existingTeams) ? [...existingTeams, teamId] : [teamId];
    await setDocument(USERS_COLLECTION, leaderId, { teams: updatedTeams }, true);
    return { id: teamId, ...teamData };
}

export async function joinTeamByCode(userId, rawCode) {
    if (!userId) {
        throw new Error("Invalid user");
    }
    if (!rawCode || !rawCode.trim()) {
        throw new Error("Team code is required");
    }
    const code = rawCode.trim().toLowerCase();
    const userProfile = await getDocumentById(USERS_COLLECTION, userId);
    if (!userProfile || userProfile.role !== "student") {
        throw new Error("Only students can join teams");
    }
    const teams = await queryDocuments(TEAMS_COLLECTION, [["teamCodeLower", "==", code]]);
    if (!teams || teams.length === 0) {
        throw new Error("Team not found");
    }
    const team = teams[0];
    const memberIds = team.memberIds || team.members || [];
    if (memberIds.includes(userId)) {
        throw new Error("You are already a member of this team");
    }
    const updatedMembers = Array.isArray(team.members) ? [...team.members, userId] : [userId];
    const updatedMemberIds = Array.isArray(memberIds) ? [...memberIds, userId] : [userId];
    await setDocument(
        TEAMS_COLLECTION,
        team.id,
        {
            members: updatedMembers,
            memberIds: updatedMemberIds,
            updatedAt: new Date()
        },
        true
    );
    const existingUserTeams = userProfile.teams || [];
    const updatedUserTeams = Array.isArray(existingUserTeams) ? [...existingUserTeams, team.id] : [team.id];
    await setDocument(USERS_COLLECTION, userId, { teams: updatedUserTeams, updatedAt: new Date() }, true);
    return { ...team, members: updatedMembers, memberIds: updatedMemberIds };
}

export async function createAdminTeam(adminId, payload) {
    const { teamName, teamType, leaderId, memberIds } = payload || {};
    if (!adminId) {
        throw new Error("Invalid admin");
    }
    const adminProfile = await getDocumentById(USERS_COLLECTION, adminId);
    if (!adminProfile || adminProfile.role !== "admin") {
        throw new Error("Only admins can create admin teams");
    }
    if (!teamName || !teamName.trim()) {
        throw new Error("Team name is required");
    }
    if (!leaderId) {
        throw new Error("Leader is required");
    }
    const leaderProfile = await getDocumentById(USERS_COLLECTION, leaderId);
    if (!leaderProfile) {
        throw new Error("Selected leader user not found");
    }
    const allMemberIds = Array.isArray(memberIds) ? memberIds.filter((m) => !!m) : [];
    if (!allMemberIds.includes(leaderId)) {
        allMemberIds.push(leaderId);
    }
    const teamData = {
        teamName: teamName.trim(),
        teamCode: null,
        teamCodeLower: null,
        leaderId,
        leaderName: leaderProfile.displayName || leaderProfile.firstName || "",
        leaderEmail: leaderProfile.email || "",
        leaderCollege: leaderProfile.collegeName || leaderProfile.college || "",
        members: allMemberIds,
        memberIds: allMemberIds,
        teamType: teamType || "custom",
        createdBy: adminId,
        createdAt: new Date(),
        updatedAt: new Date()
    };
    const teamId = await addDocument(TEAMS_COLLECTION, teamData);
    const uniqueMemberIds = Array.from(new Set(allMemberIds));
    await Promise.all(
        uniqueMemberIds.map(async (uid) => {
            const profile = await getDocumentById(USERS_COLLECTION, uid);
            if (!profile) return;
            const existingTeams = profile.teams || [];
            if (!existingTeams.includes(teamId)) {
                const updatedTeams = [...existingTeams, teamId];
                await setDocument(USERS_COLLECTION, uid, { teams: updatedTeams, updatedAt: new Date() }, true);
            }
        })
    );
    return { id: teamId, ...teamData };
}

export async function getTeamsByUser(userId) {
    if (!userId) {
        return [];
    }
    const teams = await queryDocuments(TEAMS_COLLECTION, [["memberIds", "array-contains", userId]]);
    return teams || [];
}

export async function getAllTeams() {
    const teams = await getCollectionDocuments(TEAMS_COLLECTION);
    return teams || [];
}

export async function getTeamById(teamId) {
    if (!teamId) return null;
    return await getDocumentById(TEAMS_COLLECTION, teamId);
}

export async function updateTeamMembers(adminId, teamId, leaderId, memberIds) {
    if (!adminId) {
        throw new Error("Invalid admin");
    }
    const adminProfile = await getDocumentById(USERS_COLLECTION, adminId);
    if (!adminProfile || adminProfile.role !== "admin") {
        throw new Error("Only admins can manage team members");
    }
    const team = await getDocumentById(TEAMS_COLLECTION, teamId);
    if (!team) {
        throw new Error("Team not found");
    }
    const allMemberIds = Array.isArray(memberIds) ? memberIds.filter((m) => !!m) : [];
    if (leaderId && !allMemberIds.includes(leaderId)) {
        allMemberIds.push(leaderId);
    }
    const uniqueMemberIds = Array.from(new Set(allMemberIds));
    await setDocument(
        TEAMS_COLLECTION,
        teamId,
        {
            leaderId: leaderId || team.leaderId,
            members: uniqueMemberIds,
            memberIds: uniqueMemberIds,
            updatedAt: new Date()
        },
        true
    );
    const allUsers = await getCollectionDocuments(USERS_COLLECTION);
    await Promise.all(
        allUsers.map(async (u) => {
            const teams = u.teams || [];
            const hasTeam = teams.includes(teamId);
            const shouldHave = uniqueMemberIds.includes(u.id);
            if (hasTeam && !shouldHave) {
                const updatedTeams = teams.filter((tid) => tid !== teamId);
                await setDocument(USERS_COLLECTION, u.id, { teams: updatedTeams, updatedAt: new Date() }, true);
            }
            if (!hasTeam && shouldHave) {
                const updatedTeams = [...teams, teamId];
                await setDocument(USERS_COLLECTION, u.id, { teams: updatedTeams, updatedAt: new Date() }, true);
            }
        })
    );
    return await getDocumentById(TEAMS_COLLECTION, teamId);
}

const teamService = {
    createStudentTeam,
    joinTeamByCode,
    createAdminTeam,
    getTeamsByUser,
    getAllTeams,
    getTeamById,
    updateTeamMembers
};

export default teamService;
